<template>
	<PageDefault :gradient="region?.level"> 
		<TopControls>
			<template #left>
				<button outline @click="router.push({ name: 'stats' })">
					<FontAwesomeIcon :icon="faAngleLeft" />
					<span>Назад</span>
				</button>
			</template>
			<template #right>
				<button outline @click="goToSection(sectionRealtime)">
					<FontAwesomeIcon :icon="faCircleDot" :fade="true" />
					<span>Поточні дані</span>
				</button>
				<button outline @click="goToSection(sectionStatistics)">
					<FontAwesomeIcon :icon="faChartLine" />
					<span>Статистика</span>
				</button>
				<button outline @click="goToSection(sectionStations)">
					<FontAwesomeIcon :icon="faListUl" />
					<span>Станції</span>
				</button>
			</template>
		</TopControls>

		<br />

		<div v-if="dataLoaded">
			<h1>{{ region.name }}</h1>
	
			<div>
				<div v-if="region.online_stations > 0">
					Наразі працює {{ getStationWord(region.online_stations) }} моніторингу
				</div>
				<div v-else>
					На жаль, наразі в регіоні не працює ні одна станція і це дуже сумно...
				</div>
			</div>
	
			<br /><br />
	
			<div v-if="region.online_stations > 0" ref="sectionRealtime">
				<h2>Зараз повітря {{ uaqiDescriptions[region?.level]?.air }}</h2>
	
				<div>
					<p>{{ uaqiDescriptions[region?.level]?.description }}</p>
				</div>
	
				<div class="flex flex-row gap-1 overflow-x-auto">
					<PollutantValue 
						v-for="p in pollutantsFiltered"
						:key="p"
						:pollutant=p
					/>
				</div>
	
				<br />
	
				<h2>Рекомендації</h2>
	
				<div v-if="region?.level > 0">
					<p class="flex flex-row gap-1">
						<button 
							outline
							:selected = "recommendationsType == 'all' ? true : null"
							@click="recommendationsType = 'all'"
						>
							Для всіх громадян
							<FontAwesomeIcon :icon="faUserGroup" />
						</button>
						<button 
							outline
							:selected = "recommendationsType == 'risk' ? true : null"
							@click="recommendationsType = 'risk'">
							Для груп ризику
							<FontAwesomeIcon :icon="faPersonCane" />
						</button>
					</p>
		
					<div class="flex flex-col md:flex-row gap-2">
						<PanelBox
							v-for="r in recommendationsTemplate"
							:key="r"
							class="p-3 relative overflow-hidden border w-full"
						>
							<div class="absolute bottom-0 right-0 pb-3" style="transform: rotate(5deg) scale(130%);">
								<FontAwesomeIcon :icon="r.icon" class="text-6xl text-foreground/5" />
							</div>
							<div>
								<h3 class="mt-1">
									{{ r.name }}
								</h3>
							</div>
							<div>
								<small class="text-muted-foreground">
								<p 
									style="margin-top: 0.2em; margin-bottom: 0.5em;"
									v-for="l in r.recommendations.split('\n')" :key="l"
								>
									• {{ l }}
								</p>
								</small>
							</div>
						</PanelBox>
					</div>
				</div>
				<div v-else>
					<p>На жаль, наразі немає жодних рекомендацій.</p>
				</div>
			</div>
		</div>
		<div v-else>
			<h2 class="mt-8">
				<SkeletonLoader class="h-8 w-[40%]"/>
			</h2>

			<SkeletonLoader class="h-4 w-[30%]"/>

			<br><br><br>

			<h2>
				<SkeletonLoader class="h-6 w-[45%]"/>
			</h2>

			<div>
				<SkeletonLoader class="h-4 w-[80%]"/>
				<SkeletonLoader class="h-4 w-[90%]"/>
				<SkeletonLoader class="h-4 w-[60%]"/>
			</div>

			<br>

			<div class="flex flex-row gap-1 overflow-x-auto">
				<SkeletonLoader class="h-[140px] w-[120px]"/>
				<SkeletonLoader class="h-[140px] w-[120px]"/>
				<SkeletonLoader class="h-[140px] w-[120px]"/>
				<SkeletonLoader class="h-[140px] w-[120px]"/>
				<SkeletonLoader class="h-[140px] w-[120px]"/>
				<SkeletonLoader class="h-[140px] w-[120px]"/>
			</div>

			<br>

			<h2>
				<SkeletonLoader class="h-6 w-[40%]"/>
			</h2>

			<p class="flex flex-row gap-1">
				<SkeletonLoader class="h-8 w-48"/>
				<SkeletonLoader class="h-8 w-48"/>
			</p>

			<div class="flex flex-col md:flex-row gap-2">
				<SkeletonLoader class="h-64 w-full"/>
				<SkeletonLoader class="h-64 w-full"/>
				<SkeletonLoader class="h-64 w-full"/>
			</div>
		</div>

		<br/>

		<div ref="sectionStatistics" v-if="stationsGraphs?.stations.length > 0">
			<h2>Статистика</h2>
			<div>
				<div>
					<h3>Графік забруднення за останні 30 днів</h3>
	
					<div class="flex flex-row gap-1 overflow-x-auto">
						<button
							outline
							:selected="selectedGraph.type == p.type && selectedGraph.unit == p.unit ? true : null"
							v-for="p in region.pollutants?.filter(e => pollutantsToHide.includes(e.type) == false)"
							:key="p"
							@click="selectedGraph = p"
						>
							{{ p.type }}
						</button>
					</div>

					<br>
	
					<div>
						<PlotlyChart
							v-if="dataMonth != null" 
							:data="dataMonth.data"
							:title="'-'"
							:layout="{ shapes: dataMonth.shapes, yaxis: { title: dataMonthTitle } }"
							:fixedRange=true
							style="height: 18em;"
						/>
						<SkeletonLoader v-else class="h-[18em] w-full rounded-xl" />
					</div>
					<GraphExport 
						:plotly-data="dataMonth.data" 
						:plotly-layout="{ shapes: dataMonth.shapes, yaxis: { title: dataMonthTitle } }" 
						:title="`Графік ${dataMonthTitle.substring(0, dataMonthTitle.indexOf('(')).trim()} в області: ${region.name.replace(' область', '')}`"
						:subtitle="`Дані EcoCity за останні 30 днів з ${getStationWord(dataMonth.data.length)}. <br>Головною лінією показане середнє значення. Другорядними - значення з кожної окремої станції.`"
					/>
				</div>
	
				<br/>
	
				<div>
					<h3>Календар забруднення за рік</h3>
	
					<div class="flex flex-row gap-1 overflow-x-auto">
						<button
							outline
							:selected="calendarType.type == t.type ? true : null"
							v-for="t in calendarTypes"
							:key="t"
							@click="calendarType = t"
						>
							{{ t.name }}
						</button>
					</div>
	
					<div style="margin-top: 1em;">
						<PollutionCalendar :data="calendarData" :type="calendarType.type" />
					</div>
				</div>
			</div>
		</div>
		<br/>
		<div ref="sectionStations" v-if="region.installed_stations > 0">
			<h2>Станції</h2>
			<p>Всього в регіоні встановлено {{ getStationWord(region.installed_stations) }} </p>
			<div class="grid grid-cols-1 sm:grid-cols-2 gap-2" v-if="region.installed_stations > 0">
				<div style="margin-bottom: 0.5em;">
					<div class="stationList" style="max-height: 20em;">
						<StationListItem
							v-for="station in region.stations"
							:key="station.id"
							:station="station"
							style="margin-bottom: 0.5em;"
							@mouseover="hoveredStationId = station.id" 
							@click="router.push({ name: 'station', params: { id: station.id } })"
						>	
						</StationListItem>
					</div>
				</div>
				<div style="height: 20em;">
					<RegionMap 
						v-if="dataLoaded"
						:stations="region.stations" 
						:hoveredStationId="hoveredStationId"
						style="border-radius: 1em; overflow: hidden;"
					/>
				</div>
			</div>
		</div>
		<br/><br/><br/><br/>
	</PageDefault>
</template>

<script setup>
import { ref, defineProps, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons'
import { faPersonCane } from '@fortawesome/free-solid-svg-icons'
import { faUserGroup } from '@fortawesome/free-solid-svg-icons'
import { faCircleDot } from '@fortawesome/free-solid-svg-icons'
import { faChartLine } from '@fortawesome/free-solid-svg-icons'
import { faListUl } from '@fortawesome/free-solid-svg-icons'
import { faHome } from '@fortawesome/free-solid-svg-icons'
import { faTreeCity } from '@fortawesome/free-solid-svg-icons'
import { faLungsVirus } from '@fortawesome/free-solid-svg-icons'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'

import { uaqiDescriptions } from '@/modules/UAQI.js'
import { getStationWord } from '@/modules/Language.js'
import { getLevelsShapes } from '@/modules/PlotlyUtils.js'

import PageDefault from '@/components/ui/PageDefault.vue'
import PanelBox from '@/components/ui/PanelBox.vue'
import TopControls from '@/components/ui/TopControls.vue'
import SkeletonLoader from '@/components/ui/SkeletonLoader.vue'

import StationListItem from '@/components/StationListItem.vue'
import RegionMap from '@/components/RegionMap.vue'
import PollutantValue from '@/components/PollutantValue.vue'
import PollutionCalendar from '@/components/charts/PollutionCalendar.vue'
import PlotlyChart from '@/components/charts/PlotlyChart.vue'
import GraphExport from '@/components/GraphExport.vue'

const router = useRouter()

const props = defineProps({ id: String })

const recommendationsType = ref("all")

const recommendationsTemplate = computed(() => {
	// Get recommendations object
	const recommendations = uaqiDescriptions[region.value?.level]?.recommendations

	// Skip if level is zero or no recommendations
	if (recommendations == null) return null

	let template = [
		{
			type: "outdoor",
			name: "На вулиці",
			icon: faTreeCity
		},
		{
			type: "indoor",
			name: "В приміщенні",
			icon: faHome
		},
		{
			type: "in_case_of_symptoms",
			name: "При загостренні",
			icon: faLungsVirus
		},
		{
			type: "additional",
			name: "Додатково",
			icon: faCircleInfo
		}
	]

	// Remove from view if no recommendations
	template = template.filter(e => (recommendations[e.type])?.[recommendationsType.value] != null)

	// Add recommendations texts
	template = template.map(e => ({ ...e, recommendations: recommendations[e.type][recommendationsType.value] }))

	return template
})

const pollutantsToHide = [ "Temperature", "Humidity", "Pressure" ]

const pollutantsFiltered = computed(() => {
	return [...region.value?.pollutants]?.filter(item => !pollutantsToHide.includes(item.type));
})

const calendarTypes = [ 
	{ type: "main", name: "UAQI" }, 
	{ type: "A", name: "Мікропил" }, 
	{ type: "B", name: "Основні гази" }, 
	{ type: "C", name: "Інші гази" }, 
	{ type: "D", name: "Радіаційний фон" } 
] 
const calendarType = ref(calendarTypes[0])

const region = ref({})

const hoveredStationId = ref(null)

const sectionRealtime = ref(null)
const sectionStatistics = ref(null)
const sectionStations = ref(null)

function goToSection(section) {
	section.scrollIntoView({ behavior: 'smooth' })
}

const dataLoaded = ref(false)

// Load region data
const fetchData = async () => {
	try {
		// const response = await axios.get(`/test_data/region${props.id}.json`)
		const response = await axios.get(`/api/v1/region?region_name=${props.id}`)
		region.value = response.data

		dataLoaded.value = true
	} catch (error) {
		console.error('Error fetching REGION: ', error)
	}
}

const stationsGraphs = ref(null)
const calendarData = ref(null)

const selectedGraph = ref({ type: "PM2.5", unit: "µg/m³" })
const dataMonthTitle = computed(() => {
	return selectedGraph.value.type + " (" + selectedGraph.value.unit + ")"
})

const dataMonth = computed(() => {
	if (stationsGraphs.value == null || stationsGraphs.value.stations.length < 1) return

	let average = {
		y: [],
		x: stationsGraphs.value.timestamps,
		marker: { color: "primary" },
		line: { width: 3 }, 
		name: "Середнє"
	}

	let data = []
	
	stationsGraphs.value.stations.forEach(e => {
		// FIXME: Ytor 
		if (region.value.stations.find(s => s.id == e.id && s.service == true)) {
			return
		}

		const series = e.series.find(s => 
			s.type == selectedGraph.value.type && 
			s.unit == selectedGraph.value.unit
		)

		if (series == null) return

		average.y.push(series.values)

		data.push(
			{
				x: stationsGraphs.value.timestamps,
				y: series.values,
				marker: { color: "secondary" },
				name: e.name,
			}
		)
	})

	let average_y = []

	const arrayAverage = arr => {
		const filtered = arr.filter(num => num !== null && num !== undefined)
		return filtered.reduce((p, c) => p + c, 0) / filtered.length
	}

	for (let i = 0; i < average.x.length; i++)
	{
		average_y.push(arrayAverage(average.y.map(e => e[i])))
	}

	average.y = average_y

	data.push(average)

	let levels = null
	let limitsForShapes = null

	for (const station of stationsGraphs.value.stations) {
		for (const s of station.series.filter(s => s.type == selectedGraph.value.type && s.unit == selectedGraph.value.unit)) {

			if (levels == null) levels = s.levels

			const max = Math.max(...s.values.filter(v => v != null))
			const min = Math.min(...s.values.filter(v => v != null))

			if (limitsForShapes == null) {
				limitsForShapes = { min, max }
			} else {
				limitsForShapes.min = limitsForShapes.min > min ? min : limitsForShapes.min
				limitsForShapes.max = limitsForShapes.max < max ? max : limitsForShapes.max
			}
		}
	}
	
	const shapes = getLevelsShapes({ levels }, limitsForShapes)
	
	return { data, shapes }
})

const fetchStats = async () => {
	try {
		const response = await axios.get(`/api/v1/graphs/average_30d_1d?region_name=${props.id}`)
		stationsGraphs.value = response.data
	} catch (error) {
		console.error('Error fetching STATS: ', error)
	}
}

const fetchCalendar = async () => {
	try {
		const response = await axios.get(`/api/v1/graphs/average_365d_1d?region_name=${props.id}`)
		calendarData.value = response.data
	} catch (error) {
		console.error('Error fetching CALENDAR: ', error)
	}
}

onMounted(async () => {
	// Load data
	await fetchData()
	fetchStats()
	fetchCalendar()
})
</script>

<style scoped>

.stationList {
	overflow-y: auto;
	padding-top: 0.3em;

	overflow-y: overlay;
}
</style>